import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { staffAuth as appStaffAuth } from '../lib/auth';
import type { StaffAuth, StaffAuthState } from '../lib/auth';

// React binding for the staff auth boundary (lib/auth/staffAuth.ts).
//
// The store is external and already has subscribe/getState, so
// useSyncExternalStore is the whole implementation — no duplicated state, and
// no chance of a component rendering a stale signed-in badge after sign-out.
//
// Components use THIS, never MSAL. That is the point of the boundary: if the
// identity library is ever swapped, the surface below is what has to keep
// working, and it contains no Microsoft concepts.

export interface UseStaffAuth {
  state: StaffAuthState;
  /** True when Entra is configured and the flow can be used. */
  available: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Acquire a bearer token for the Starpoint API, or null when signed out. */
  getApiToken: () => Promise<string | null>;
}

export function useStaffAuth(auth: StaffAuth = appStaffAuth): UseStaffAuth {
  const state = useSyncExternalStore(
    useCallback((cb) => auth.subscribe(cb), [auth]),
    useCallback(() => auth.getState(), [auth]),
    useCallback(() => auth.getState(), [auth]),
  );

  // An MSAL session survives a page reload, so ask once on mount whether one
  // is already there. Does nothing when Entra is not configured.
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    if (restored || !auth.available) return;
    setRestored(true);
    void auth.restore();
  }, [auth, restored]);

  return {
    state,
    available: auth.available,
    signIn: useCallback(async () => {
      await auth.signIn();
    }, [auth]),
    signOut: useCallback(async () => {
      await auth.signOut();
    }, [auth]),
    getApiToken: useCallback(() => auth.getApiToken(), [auth]),
  };
}
